import type { QueryClient } from '@tanstack/react-query';
import type { DashboardRole } from '@/types/session';
import type {
  DashboardOrganisationType,
  DashboardUserRow,
  DashboardUserStatus,
} from '@/types/dashboardUser';
import { userQueryKey, usersListQueryKey, type UsersPage } from '@/api/endpoints/users';

export const USERS_PAGE_SIZE = 25;

/**
 * Patches a row across every cached GET /api/v1/users list page (any
 * skip/take/search/filter combination) and its GET /api/v1/users/{id} detail
 * cache after a suspend, so the change is visible immediately without waiting
 * for every open list or detail view to refetch.
 */
export function patchCachedUserRow(
  queryClient: QueryClient,
  id: string,
  updater: (row: DashboardUserRow) => DashboardUserRow,
): void {
  queryClient.setQueriesData<UsersPage>({ queryKey: usersListQueryKey }, (page) =>
    page ? { ...page, items: page.items.map((row) => (row.id === id ? updater(row) : row)) } : page,
  );
  queryClient.setQueryData<DashboardUserRow>(userQueryKey(id), (row) => (row ? updater(row) : row));
}

/**
 * Removes a row from every cached GET /api/v1/users list page and drops its
 * GET /api/v1/users/{id} detail cache after a delete.
 */
export function removeCachedUserRow(queryClient: QueryClient, id: string): void {
  queryClient.setQueriesData<UsersPage>({ queryKey: usersListQueryKey }, (page) =>
    page
      ? {
          ...page,
          items: page.items.filter((row) => row.id !== id),
          total: Math.max(0, page.total - 1),
        }
      : page,
  );
  queryClient.removeQueries({ queryKey: userQueryKey(id) });
}

export interface Organisation {
  id: string;
  name: string;
}

export interface RoleOrganisationMapping {
  organisationType: DashboardOrganisationType | null;
  organisations: Organisation[];
}

/**
 * Stands in for an organisation directory endpoint, which does not exist yet
 * (no doc in the backend repo documents one). Keyed by DashboardRole so the
 * invite form can offer all six roles while resolving each to the right
 * organisationType.
 *
 * ASSUMPTION: only GroundHandler (-> GroundHandler table) and SenderCustomer
 * (-> FulfillmentCenter table) have a documented organisation source
 * (docs/dashboard/dashboard-invite-and-member-user-tables-plan.md in the
 * backend repo). Driver, ReceivingTerminal and LastMileCustomerService have no
 * resolved organisation source yet; they're mapped to organisationType
 * 'GroundHandler' here purely as a placeholder so the invite form has
 * something to submit. Replace once the backend clarifies their org source.
 */
export const ORGANISATIONS_BY_ROLE: Record<DashboardRole, RoleOrganisationMapping> = {
  Tollit: { organisationType: null, organisations: [] },
  GroundHandler: {
    organisationType: 'GroundHandler',
    organisations: [
      { id: 'org-bkk-ground-services', name: 'Bangkok Ground Services' },
      { id: 'org-svb-ramp-services', name: 'Suvarnabhumi Ramp Services' },
    ],
  },
  SenderCustomer: {
    organisationType: 'FulfillmentCenter',
    organisations: [
      { id: 'org-siam-textiles', name: 'Siam Textiles Co.' },
      { id: 'org-metro-lastmile', name: 'Metro Last-Mile Co.' },
    ],
  },
  // ASSUMPTION — see doc comment above.
  Driver: {
    organisationType: 'GroundHandler',
    organisations: [
      { id: 'org-nordic-air-cargo', name: 'Nordic Air Cargo' },
      { id: 'org-siam-freight-movers', name: 'Siam Freight Movers' },
    ],
  },
  // ASSUMPTION — see doc comment above.
  ReceivingTerminal: {
    organisationType: 'GroundHandler',
    organisations: [{ id: 'org-svb-terminal-ops', name: 'Suvarnabhumi Terminal Ops' }],
  },
  // ASSUMPTION — see doc comment above.
  LastMileCustomerService: {
    organisationType: 'GroundHandler',
    organisations: [{ id: 'org-metro-lastmile', name: 'Metro Last-Mile Co.' }],
  },
};

/**
 * Best-effort display name for a user row's organisationType/organisationKey,
 * resolved against the mock organisation table above. Real invited users
 * won't exist in that table (there's no lookup endpoint yet), so this falls
 * back to showing the raw type/key pair.
 */
export function organisationNameFor(
  organisationType: DashboardOrganisationType | null,
  organisationKey: string | null,
): string | null {
  if (!organisationType || !organisationKey) return null;

  for (const mapping of Object.values(ORGANISATIONS_BY_ROLE)) {
    if (mapping.organisationType !== organisationType) continue;
    const match = mapping.organisations.find((org) => org.id === organisationKey);
    if (match) return match.name;
  }

  return `${organisationType} · ${organisationKey}`;
}

/** A user row flattened into what the admin screens actually render. */
export interface DisplayUser {
  id: string;
  name: string;
  email: string;
  role: DashboardRole;
  organisationKey: string | null;
  organisationName: string | null;
  status: DashboardUserStatus;
  dateAdded: string;
}

/**
 * Maps an API row to the shape the list and detail screens render. Pure: every
 * edit now goes through PUT /api/v1/users/{id} and comes back as a row, so
 * there is no local overlay of unsaved changes to merge in.
 */
export function toDisplayUser(row: DashboardUserRow): DisplayUser {
  return {
    id: row.id,
    name: row.displayName,
    email: row.email,
    role: row.userType,
    organisationKey: row.organisationKey,
    organisationName: organisationNameFor(row.organisationType, row.organisationKey),
    status: row.status,
    dateAdded: row.dateAdded,
  };
}

export const STATUS_BADGE_CLASS: Record<DashboardUserStatus, string> = {
  Active: 'border-success/40 bg-success/15 text-success',
  Invited: 'border-warning/40 bg-warning/15 text-warning',
  Deactivated: 'border-destructive/40 bg-destructive/15 text-destructive',
};

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
